import { Box, Tabs } from '@mui/material'
import { StyledTab } from 'components/StyledTab/StyledTab'
import TabPanel from 'components/TabPanel/TabPanel'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectMissionEditingZone } from 'store/selectors/mission'
import {
  DEFAULT_FRONT_DUST_SUPPRESSION,
  DEFAULT_TURBINE_SPEED,
  MISSION_PAGE_MODES,
  MISSION_ZONE_TYPES,
  SHOW_ZONE_SETTINGS_TAB,
} from 'utils/constants'
import { ICleaningZone, IZone, RootState } from 'utils/interfaces'

import ZoneDetail from './ZoneDetails/ZoneDetails'
import ZoneEquipmentSettings from './ZoneDetails/ZoneEquipmentSettings'
import { OnSaveClickedParams, ZoneFormCache, getLatestZone } from './types'

const TAB_PANEL_BOX_SX = {
  p: 3,
  width: '100%',
  boxSizing: 'border-box',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
} as const

interface DrawZoneModeProps {
  onSaveClicked: (
    zoneName: string,
    zoneDescription: string,
    zoneType: MISSION_ZONE_TYPES,
    cleaningSpeed?: number,
    transportSpeed?: number,
    isBorderCleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean }
  ) => Promise<void>
  zone?: IZone
}

const DrawZoneMode = ({ onSaveClicked, zone }: DrawZoneModeProps) => {
  const adaptedOnSaveClicked = (params: OnSaveClickedParams) => {
    return onSaveClicked(
      params.zoneName,
      params.zoneDescription,
      params.zoneType,
      params.cleaningSpeed,
      params.transportSpeed,
      params.isBorderCleaning,
      params.equipment_setup
    )
  }
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)
  const editingZone = useSelector(selectMissionEditingZone)
  const missionMode = useSelector((state: RootState) => state.mission.mode)
  const drawingZoneType = useSelector((state: RootState) => state.mission.drawingZoneType)
  const isRouteMode =
    missionMode === MISSION_PAGE_MODES.EDIT_ZONE_ROUTE || missionMode === MISSION_PAGE_MODES.DRAW_ROUTE

  const zoneToCheck = editingZone || zone

  // Determine the zone type from existing zone if available, otherwise fall back to drawingZoneType from the store
  const zoneType = zoneToCheck?.properties?.zone_type ?? drawingZoneType
  // Show Settings tab if feature toggle is enabled AND zone is not restricted
  const isRestrictedZone = zoneType === MISSION_ZONE_TYPES.RESTRICT
  const showSettingsTab = SHOW_ZONE_SETTINGS_TAB && !isRestrictedZone

  // Determine the first tab label based on the mode
  const firstTabLabel = isRouteMode
    ? t('mission.route_editing')
    : zoneType === MISSION_ZONE_TYPES.CLEANING
    ? t('mission.cleaning_zone_editing')
    : t('mission.restrict_zone_editing')

  const cleaningZones = useSelector((state: RootState) => state.mission.cleaningZones)
  const latestZone = getLatestZone(cleaningZones, zoneToCheck?.properties?.zone_id)
  const finalZone = latestZone || zoneToCheck

  const formCacheRef = useRef<ZoneFormCache>({})
  const zoneIdRef = useRef<number | undefined>(zoneToCheck?.properties?.zone_id)

  useEffect(() => {
    const currentZoneId = zoneToCheck?.properties?.zone_id
    if (zoneIdRef.current !== currentZoneId) {
      zoneIdRef.current = currentZoneId
      formCacheRef.current = {}

      if (finalZone) {
        const zone = finalZone as ICleaningZone
        const props = zone.properties
        if (props) {
          if (props.name) formCacheRef.current.name = props.name
          if (props.description) formCacheRef.current.description = props.description
          if (props.zone_type) formCacheRef.current.selectedZoneType = props.zone_type as MISSION_ZONE_TYPES
          if (props.cleaning_speed !== undefined) formCacheRef.current.cleaningSpeed = props.cleaning_speed
          if (props.transport_speed !== undefined) formCacheRef.current.transportSpeed = props.transport_speed
          if (props.is_border_cleaning !== undefined) formCacheRef.current.isBorderCleaning = props.is_border_cleaning
          if (props.equipment_setup) {
            const eq = props.equipment_setup
            if (eq.turbine_speed !== undefined) formCacheRef.current.turbineSpeed = eq.turbine_speed
            if (eq.front_dust_suppression !== undefined)
              formCacheRef.current.frontDustSuppression = eq.front_dust_suppression
          }
        }
      }
    }
  }, [finalZone, zoneToCheck, cleaningZones])

  const getCacheValue = <K extends keyof ZoneFormCache>(key: K): ZoneFormCache[K] => {
    return formCacheRef.current[key]
  }

  const setCacheValue = <K extends keyof ZoneFormCache>(key: K, value: ZoneFormCache[K]) => {
    formCacheRef.current[key] = value
  }

  const getAllCacheValues = (): ZoneFormCache => {
    return { ...formCacheRef.current }
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
  }

  return (
    <div className="flex-column">
      {showSettingsTab && (
        <Tabs
          value={tab}
          onChange={handleChange}
          variant="fullWidth"
          indicatorColor="primary"
          sx={{ height: 45, minHeight: 45 }}
        >
          <StyledTab
            label={firstTabLabel}
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0, maxWidth: 200 }}
            disableRipple
          />
          <StyledTab
            label={'Settings'}
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
            disableRipple
          />
        </Tabs>
      )}
      {showSettingsTab ? (
        <>
          <TabPanel value={tab} index={0} disablePadding={true}>
            <Box sx={TAB_PANEL_BOX_SX}>
              <ZoneDetail
                onSaveClicked={adaptedOnSaveClicked}
                zone={zone}
                getCacheValue={getCacheValue}
                setCacheValue={setCacheValue}
                getAllCacheValues={getAllCacheValues}
              />
            </Box>
          </TabPanel>
          <TabPanel value={tab} index={1} disablePadding={true}>
            <Box sx={TAB_PANEL_BOX_SX}>
              <ZoneEquipmentSettings
                onSaveClicked={adaptedOnSaveClicked}
                zone={zone}
                getCacheValue={getCacheValue}
                setCacheValue={setCacheValue}
                getAllCacheValues={getAllCacheValues}
              />
            </Box>
          </TabPanel>
        </>
      ) : (
        <Box sx={TAB_PANEL_BOX_SX}>
          <ZoneDetail
            onSaveClicked={adaptedOnSaveClicked}
            zone={zone}
            getCacheValue={getCacheValue}
            setCacheValue={setCacheValue}
            getAllCacheValues={getAllCacheValues}
          />
        </Box>
      )}
    </div>
  )
}

export default DrawZoneMode
